import legacyUa from '../ua.json'

/**
 * Expand a flat dictionary with dotted keys (e.g. `"action.audio_not_found"`)
 * into a nested object that i18next can resolve with the default
 * `keySeparator: '.'` setting.
 */
function expand(flat: Record<string, string>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(flat)) {
    const parts = key.split('.')
    let cur = out
    for (let i = 0; i < parts.length - 1; i++) {
      const p = parts[i]!
      if (typeof cur[p] !== 'object' || cur[p] === null) {
        cur[p] = {}
      }
      cur = cur[p] as Record<string, unknown>
    }
    cur[parts[parts.length - 1]!] = value
  }
  return out
}

/**
 * Default UI language. Combines:
 *  - Migrated legacy keys from `creo-studio/frontend/static/js/lang-data.js`
 *    (E01.10 migration script, ~2436 keys, dotted-flat → expanded nested).
 *  - App-specific React keys (errors, nav, test, etc.) added on top so they
 *    can override or extend legacy where needed.
 */
export const ua = {
  translation: {
    ...expand(legacyUa as Record<string, string>),
    // App-specific overrides / extensions:
    common: {
      ...((expand(legacyUa as Record<string, string>).common as Record<string, unknown>) ?? {}),
      loading: 'Завантаження...',
      error: 'Помилка',
      cancel: 'Скасувати',
      save: 'Зберегти',
      delete: 'Видалити',
      retry: 'Повторити',
      actions: {
        save: 'Зберегти',
        cancel: 'Скасувати',
        delete: 'Видалити',
      },
      'error.generic': 'Сталася помилка. Спробуйте ще раз.',
      unsaved: {
        title: 'Незбережені зміни',
        body: 'Ви маєте незбережені зміни. Перемкнувши workspace, ви їх втратите.',
        confirm: 'Перемкнути',
        cancel: 'Скасувати',
      },
    },
    auth: {
      ...((expand(legacyUa as Record<string, string>).auth as Record<string, unknown>) ?? {}),
      login: {
        title: 'Увійти',
        subtitle: 'Вітаємо в Creo Studio',
        username: 'Логін',
        username_placeholder: 'Введіть логін',
        password: 'Пароль',
        password_placeholder: 'Введіть пароль',
        password_show: 'Показати пароль',
        password_hide: 'Сховати пароль',
        submit: 'Увійти',
        forgot: 'Забули пароль?',
        'no-account': 'Немає акаунту?',
        'register-link': 'Зареєструватись',
        error: {
          invalid: 'Невірний логін або пароль',
          network: 'Помилка мережі. Спробуйте ще раз.',
        },
      },
      forgot: {
        title: 'Забули пароль?',
        body: 'Введіть ваш username — ми зв’яжемося з адміном через Telegram для генерації тимчасового пароля.',
        username: 'Логін',
        submit: 'Надіслати запит',
        success: 'Запит надіслано. Очікуйте на повідомлення в Telegram.',
        error_required: 'Введіть логін',
        'back-to-login': 'Згадали пароль? Увійти',
      },
      logout: {
        success: 'Ви вийшли',
      },
      invite: {
        accepting: 'Прийняття запрошення...',
        success: 'Ви приєдналися до {{name}}',
        error_expired: 'Запрошення прострочене. Попросіть нове.',
        error_invalid: 'Запрошення недійсне або вже використане.',
        error_already_member: 'Ви вже є учасником цього робочого простору.',
        go_dashboard: 'На головну',
        go_login: 'Увійти',
      },
      register: {
        title: 'Створити акаунт',
        username: 'Логін',
        username_checking: 'Перевірка...',
        username_available: 'Логін доступний',
        password: 'Пароль',
        confirm_password: 'Повторіть пароль',
        passwords_mismatch: 'Паролі не співпадають',
        email: 'Email',
        email_required: 'Вкажіть коректний email',
        tos_accept: 'Я погоджуюся з умовами використання',
        submit: 'Створити',
        have_account: 'Маєте акаунт?',
        login_link: 'Увійти',
        success: 'Реєстрація успішна. Адмін повинен схвалити акаунт.',
        error_username_taken: 'Цей логін вже зайнятий',
        error_email_taken: 'Email вже використовується',
        password_strength_prefix: 'Надійність:',
        password_too_weak: 'занадто слабкий',
        password_strength: {
          weak: 'Слабкий',
          medium: 'Середній',
          strong: 'Сильний',
          very_strong: 'Дуже сильний',
        },
      },
    },
    settings: {
      title: 'Налаштування',
      tabs: {
        account: 'Акаунт',
        apikeys: 'API ключі',
      },
      apikeys: {
        title: 'API ключі',
        description:
          'Ключі для зовнішніх сервісів. Зберігаються зашифрованими у workspace. Тільки адмін може додавати та видаляти.',
        empty: 'Ключів ще немає. Додайте перший.',
        not_admin: 'Тільки адмін workspace може керувати ключами.',
        no_workspace:
          'Виберіть workspace у заголовку — ключі прив’язані до робочого простору.',
        column: {
          provider: 'Сервіс',
          value: 'Ключ',
          created: 'Додано',
          actions: 'Дії',
        },
        provider_label: 'Сервіс',
        key_label: 'Ключ',
        provider: {
          elevenlabs: 'ElevenLabs',
          fal: 'fal.ai',
          deepl: 'DeepL',
        },
        add_button: 'Додати ключ',
        cancel: 'Скасувати',
        delete: 'Видалити',
        delete_confirm: 'Видалити ключ {{provider}}?',
        add_success: 'Ключ додано',
        add_error: 'Не вдалося додати ключ',
        delete_success: 'Ключ видалено',
        delete_error: 'Не вдалося видалити',
        load_error: 'Не вдалося завантажити ключі',
        key_required: 'Введіть ключ',
      },
      password: {
        heading: 'Зміна пароля',
        current: 'Поточний пароль',
        new: 'Новий пароль',
        confirm: 'Повторіть новий пароль',
        submit: 'Змінити пароль',
        success: 'Пароль змінено',
        error_current_wrong: 'Поточний пароль невірний',
        error_mismatch: 'Паролі не співпадають',
        error_too_weak: 'Пароль занадто слабкий',
        coming_soon:
          'Coming soon — endpoint pending Go-fix (parity-gap row 2).',
      },
    },
    errors: {
      something_wrong: 'Щось пішло не так',
      reload: 'Перезавантажити сторінку',
      support_id: 'ID для підтримки: {{id}}',
      not_found: 'Сторінку не знайдено.',
      forbidden: 'Доступ заборонено.',
      server_error: 'Помилка сервера. Спробуйте пізніше.',
      go_home: 'На головну',
    },
    autoUpdate: {
      title: 'Доступне оновлення',
      countdown: 'Перезавантаження через {{n}}',
    },
    nav: {
      dashboard: 'Дашборд',
      projects: 'Проєкти',
      data: 'Дані',
      quickTasks: 'Швидкі задачі',
      voices: 'Голоси',
      overlay: 'Блики',
      compress: 'Стиснення відео',
      audioMask: 'Маскування аудіо',
      transcription: 'Транскрипція',
      translate: 'Переклад',
      templates: 'Шаблони',
      stats: 'Статистика',
      settings: 'Налаштування',
      notifications: 'Сповіщення',
      admin: 'Адмінка',
      workspace: 'Робочий простір',
      updates: 'Оновлення',
      support: 'Баги та ідеї',
      faq: 'FAQ',
    },
    sidebar: {
      label: 'Основна навігація',
      section_main: 'Основне',
      section_data: 'Дані',
      section_system: 'Система',
    },
    header: {
      menu: 'Меню',
      stats: 'Міні-статистика',
      personal: 'Особистий простір',
      theme: 'Тема',
      language: 'Мова',
      logout: 'Вийти',
      settings_soon: 'Налаштування скоро',
      switcher_soon: 'Перемикач workspace — у E2',
      bell_soon: 'Сповіщення — у E3',
      // Mini-stats labels — verbatim from legacy `lang-data.js` (`hdr.*`).
      // Kept in sync with the dotted-flat `hdr.*` keys in `ua.json` so
      // either path resolves to the same string.
      running: 'В РОБОТІ',
      queue: 'ЧЕРГА',
      errors: 'ПОМИЛКИ',
      done: 'Готово',
      running_tip: 'В роботі — відкрити завдання',
      queue_tip: 'В черзі — відкрити завдання',
      errors_tip: 'Помилок сьогодні — відкрити завдання',
      done_tip: 'Всього виконано — відкрити завдання',
      ws_personal: 'Особистий',
      ws_title: 'Робоче середовище',
      ws_manage: 'Управління середовищами',
      search: 'Пошук',
    },
    test: {
      greeting: 'Привіт',
      welcome: 'Привіт, {{name}}',
    },
    notif: {
      task_done: 'Задача {{taskId}} завершена',
    },
    notifications: {
      title: 'Сповіщення',
      bell_aria: 'Сповіщення',
      empty: 'Сповіщень немає.',
      empty_unread: 'Усе прочитано.',
      error_load: 'Не вдалося завантажити сповіщення.',
      panel_title: 'Сповіщення',
      see_all: 'Усі сповіщення',
      mark_all_read: 'Позначити всі як прочитані',
      mark_read: 'Позначити прочитаним',
      mark_read_success: 'Позначено',
      mark_all_read_success: 'Позначено: {{count}}',
      tab: {
        all: 'Усі',
        unread: 'Непрочитані',
      },
      delete: 'Видалити',
      delete_success: 'Сповіщення видалено',
      delete_error: 'Не вдалося видалити',
      pagination: {
        prev: 'Попередня',
        next: 'Наступна',
        page: 'Сторінка {{page}} з {{pages}}',
      },
      announcement: {
        aria: 'Системне оголошення',
      },
      type: {
        task_done: 'Задача завершена',
        task_error: 'Помилка задачі',
        member_added: 'Учасника додано',
        colleague_added: 'Колегу додано',
        team_update: 'Оновлення команди',
        account_approved: 'Акаунт підтверджено',
        key_shared: 'Ключ розшарено',
        warning: 'Попередження',
        error_alert: 'Помилка',
        resource_high: 'Високе навантаження',
        resource_normal: 'Навантаження в нормі',
      },
    },
    balance: {
      el: 'EL',
      fal: 'FAL',
      el_label: 'EL баланс',
      fal_label: 'FAL баланс',
      pending: 'не налаштовано',
      error: 'недоступний',
      remaining: 'Залишок: {{value}}',
      remaining_currency: 'Баланс: {{value}}',
    },
    admin_monitor: {
      title: 'Системний монітор',
      running_tasks: 'Виконуються',
      pending_tasks: 'У черзі',
      disk: 'Диск',
      memory: 'Памʼять',
      error_load: 'Не вдалося завантажити монітор',
    },
    stats: {
      title: 'Статистика',
      export_button: 'Експорт XLSX',
      period: {
        today: 'Сьогодні',
        '7d': '7 днів',
        '30d': '30 днів',
        all: 'Увесь час',
      },
      kpi: {
        total_rows: 'Усього рядків',
        rows_done: 'Готово',
        rows_failed: 'Помилки',
        el_chars: 'EL символів',
        fal_minutes: 'FAL хвилин',
        voice_clones: 'Клонів голосу',
        storage: 'Сховище',
      },
      charts: {
        daily_title: 'Динаміка по днях',
        engine_title: 'Розподіл за двигуном',
        loading: 'Завантаження графіка…',
        empty: 'Даних за період немає.',
      },
      error_load: 'Не вдалося завантажити статистику.',
      mini_chart: {
        title: 'Активність черги (останні {{min}} хв)',
        running: 'Виконуються',
        queued: 'У черзі',
      },
    },
    dashboard: {
      hero: {
        greeting_morning: 'Доброго ранку',
        greeting_day: 'Доброго дня',
        greeting_evening: 'Доброго вечора',
        greeting_night: 'Доброї ночі',
        in_workspace: 'У workspace «{{name}}»',
        personal_context: 'Особистий простір',
      },
      // Ported 1:1 from legacy `dash.*` namespace in lang-data.js.
      subtitle: 'Ось що відбувається на платформі',
      updated_at: 'оновлено {{time}}',
      stats_btn: 'Статистика',
      projects_btn: 'Мої проєкти',
      onboarding: {
        welcome: 'Ласкаво просимо до AI Creo Studio!',
        welcome_hint: 'Зроби ці кроки щоб почати створювати AI відео:',
        dismiss: 'Сховати',
        step1: 'Додай ElevenLabs API ключ',
        step2: 'Додай FAL.ai API ключ',
        step3: 'Клонуй голос',
        step4: 'Створи перший проєкт',
        step5: 'Запусти обробку!',
        empty_title: 'Почни створювати AI відео!',
        empty_step1: 'Додай API ключі в налаштуваннях',
        empty_step2: 'Створи проєкт або швидке завдання',
        create_project_btn: '+ Створити проєкт',
      },
      qa: {
        quick: 'Швидке завдання',
        project: 'Створити проєкт',
        keys: 'API ключі',
        tasks: 'Усі завдання',
      },
      stats: {
        projects: 'Проєктів',
        rows: 'Рядків всього',
        audio: 'Аудіо готове',
        video: 'Відео готове',
        disk: 'Диск',
      },
      overlay: {
        title: 'Бліки на відео',
        done: 'Готово',
        queue: 'В черзі',
      },
      today: {
        title: 'Зроблено сьогодні',
        audio: 'Аудіо',
        video: 'Відео',
        errors: 'Помилки',
        chars: 'Символів',
      },
      recent: {
        title: 'Останні результати',
        all_tasks: 'Усі завдання →',
        empty: 'Немає готових файлів',
        download_audio: 'Завантажити аудіо',
        download_video: 'Завантажити відео',
      },
      recent_projects: {
        title: 'Останні проєкти',
        title_h: 'Останні проєкти',
        all: 'Всі проєкти →',
        no_desc: 'Без опису',
        rows_short: 'рядків',
        empty: 'Проєктів ще немає.',
        error_load: 'Не вдалося завантажити проєкти',
        view_all: 'Усі проєкти',
      },
      status: {
        active: 'активний',
        done: 'готово',
        archived: 'архів',
      },
      queue: {
        title: 'Черга обробки',
        idle: 'Немає завдань',
        empty: 'Активних завдань немає',
        running: 'Виконуються',
        queued: 'У черзі',
        active_n: '{{n}} активних',
        refresh: 'Оновити',
      },
      activity: {
        title: 'Остання активність',
        empty: 'Активності ще немає.',
        error_load: 'Не вдалося завантажити активність',
        no_text: '(без тексту)',
      },
    },
    projects: {
      title: 'Проєкти',
      create_button: '+ Новий проєкт',
      subtitle: 'Всі твої проєкти в одному місці',
      empty: 'Проєктів ще немає. Створіть перший!',
      empty_cta: 'Створити перший',
      no_results: 'Немає проєктів за цим запитом.',
      error_load: 'Не вдалося завантажити проєкти.',
      search_placeholder: 'Пошук за назвою або описом...',
      // List-page summary, toolbar, list states — ported 1:1 from legacy
      // `pages/projects.html` + `static/js/lang-data.js` (`proj.*` keys).
      list: {
        count_projects: 'проєктів',
        count_rows: 'рядків',
        count_audio: 'аудіо',
        count_video: 'відео',
        loading: 'Завантаження проєктів...',
        show_more: 'Показати ще',
        left: 'залишилось',
        all_status: 'Усі статуси',
        status_draft: 'Чернетка',
        status_active: 'Активний',
        status_ready: 'Готово до обробки',
        status_archive: 'Архів',
        status_empty: 'Порожній',
        all_dates: 'Усі дати',
        today: 'Сьогодні',
        d3: '3 дні',
        d7: '7 днів',
        month: 'Місяць',
        sort_updated_desc: 'Оновлено ↓',
        sort_updated_asc: 'Оновлено ↑',
        sort_title_asc: 'Назва А-Я',
        sort_title_desc: 'Назва Я-А',
        sort_rows_desc: 'Рядків ↓',
        sort_rows_asc: 'Рядків ↑',
        sort_progress: 'Прогрес ↓',
        view_tiles: 'Плитки',
        view_list: 'Список',
        no_desc: 'Без опису',
        processing: 'Обробляється',
        empty_title: 'Створи перший проєкт!',
        empty_desc:
          'Проєкт — це робочий простір для озвучки та lip-sync відео. Додай рядки з текстом і запусти обробку.',
      },
      column: {
        title: 'Назва',
        status: 'Статус',
        updated: 'Оновлено',
        actions: 'Дії',
      },
      status: {
        active: 'Активний',
        archived: 'Архівний',
        unknown: '—',
      },
      action: {
        open: 'Відкрити',
        open_menu: 'Дії над проєктом',
        delete: 'Видалити',
        pin: 'Закріпити',
        unpin: 'Відкріпити',
        duplicate: 'Дублювати',
        archive: 'Архівувати',
      },
      settings: {
        section_label: 'Налаштування пайплайну',
        el_model: 'Модель ElevenLabs',
        fal_stack: 'Модель FAL',
        loop_mode: 'Loop',
        save: 'Зберегти',
        save_success: 'Налаштування збережено',
        save_error: 'Не вдалося зберегти',
      },
      rows: {
        title: 'Рядки',
        empty: 'У цьому проєкті ще немає рядків.',
        empty_cta: 'Додати перший',
        error_load: 'Не вдалося завантажити рядки',
        add_button: 'Додати рядок',
        audio_ref_subtitle: 'Аудіо-референс',
        column: {
          number: '#',
          name: 'Назва',
          text: 'Текст',
          audio_ref: 'Аудіо-референс',
          video_ref: 'Відео для ліпсингу',
          voice: 'Голос',
          el_status: 'EL',
          fal_status: 'FAL',
          actions: 'Дії',
          select_all: 'Виділити всі',
          select_row: 'Виділити рядок #{{n}}',
        },
        cell_placeholder: {
          name: 'Назва…',
          text: 'Текст…',
          voice: 'Voice ID',
          audio_ref: 'URL аудіо',
          video_ref: 'URL відео',
        },
        action: {
          delete: 'Видалити рядок',
          launch: 'Запустити',
          cancel: 'Скасувати',
          retry: 'Повторити',
          play_output: 'Програти результат',
          download_output: 'Завантажити',
          launch_all: 'Запустити всі',
          launch_selected: 'Запустити виділені ({{n}})',
          import: 'Імпортувати',
          validate_all: 'Перевірити',
          validate_selected: 'Перевірити виділені ({{n}})',
          export_zip: 'Експорт ZIP',
          bulk_delete: 'Видалити виділені',
          clear_selection: 'Зняти виділення',
        },
        column_extra: {
          el_model: 'EL модель',
          fal_stack: 'FAL модель',
          note: 'Нотатка',
        },
        cell_placeholder_extra: {
          note: 'Нотатка…',
        },
        cancel_success: 'Скасовано',
        cancel_error: 'Не вдалося скасувати',
        retry_success: 'Повторно запущено',
        retry_error: 'Не вдалося перезапустити',
        bulk_delete_success: 'Видалено {{n}} рядків',
        bulk_delete_partial: 'Видалено {{ok}} з {{n}} (помилок: {{f}})',
        bulk_delete_confirm: {
          title: 'Видалити {{n}} рядків?',
          body: 'Усі задачі цих рядків будуть скасовані. Цю дію не можна скасувати.',
          confirm: 'Видалити',
          cancel: 'Скасувати',
        },
        export_zip_empty: 'Жоден рядок ще не завершився',
        selection_count: 'Виділено: {{n}}',
        launch_success: 'Запуск ініційовано',
        launch_error: 'Не вдалося запустити',
        download_error: 'Не вдалося завантажити',
        no_rows_to_launch: 'Немає рядків для запуску',
        delete_confirm: {
          title: 'Видалити рядок #{{n}}?',
          body: 'Усі задачі цього рядка будуть скасовані.',
          confirm: 'Видалити',
          cancel: 'Скасувати',
        },
        delete_success: 'Рядок видалено',
        delete_error: 'Не вдалося видалити рядок',
        create_success: 'Рядок додано',
        create_error: 'Не вдалося додати рядок',
        update_error: 'Не вдалося зберегти зміни',
      },
      create_dialog: {
        title: 'Новий проєкт',
        template_label: 'Шаблон',
        tpl_blank: 'Порожній',
        tpl_vsl: 'VSL озвучка',
        tpl_dips: 'Швидкі діпи',
        tpl_translate: 'Переклад відео',
        name_label: 'Назва проєкту *',
        name_placeholder: 'Наприклад: Озвучка рекламного ролика',
        description_label: 'Опис',
        description_optional: '(необовʼязково)',
        description_placeholder: 'Що за проєкт, для кого, терміни...',
        submit: 'Створити проєкт',
        submitting: 'Створюю...',
        cancel: 'Скасувати',
        success: 'Проєкт створено',
        error_name_required: 'Введи назву проєкту',
        error_generic: 'Не вдалося створити проєкт',
      },
      delete_confirm: {
        title: 'Видалити проєкт «{{name}}»?',
        body: 'Усі рядки, файли та задачі цього проєкту стануть недоступними. Цю дію не можна скасувати.',
        confirm: 'Видалити',
        cancel: 'Скасувати',
      },
      delete_success: 'Проєкт видалено',
      delete_error: 'Не вдалося видалити проєкт',
      launch: {
        confirm_title: 'Перевірка перед запуском',
        confirm_summary:
          'Готових рядків: {{ready}} з {{total}}. Запуск створить відповідну кількість задач.',
        stat_ready: 'Готові',
        stat_missing: 'Бракує',
        mode_legend: 'Режим запуску',
        mode_audio_title: 'Лише аудіо (попередньо прослухати)',
        mode_audio_hint:
          'Створює тільки EL TTS — без витрат на FAL. Корисно перевірити озвучку до повного пайплайну.',
        mode_full_title: 'Повний пайплайн (аудіо + ліпсинг)',
        mode_full_hint: 'EL TTS, потім FAL ліпсинг. Витрачає кредити в обох сервісах.',
        run: 'Запустити ({{n}})',
        cancel: 'Скасувати',
        missing_field: {
          text: 'текст',
          audio_ref: 'аудіо-референс',
          video_ref: 'відео',
        },
        missing_overflow: '… і ще {{n}}',
      },
      import: {
        title: 'Імпортувати рядки',
        subtitle:
          'Завантажте Excel, вставте таблицю з буфера або вкажіть URL Google Sheets / CSV.',
        tab: {
          xlsx: 'Файл',
          paste: 'Вставити',
          url: 'URL / Sheets',
        },
        xlsx_label: 'XLSX / CSV (≤ 500 рядків)',
        paste_label: 'Вставте таблицю (TSV/CSV)',
        paste_columns_hint:
          'Колонки: Назва · Текст · Аудіо URL · Відео URL · Voice ID. Розділювач — Tab.',
        paste_preview_empty:
          'Поки порожньо. Вставте дані з таблиці — побачите попередній перегляд.',
        paste_preview_count: 'Буде додано: {{n}} рядк(ів)',
        paste_preview_truncated: '(показано перші {{shown}})',
        preview_col_name: 'Назва',
        preview_col_text: 'Текст',
        preview_col_audio: 'Audio',
        preview_col_video: 'Video',
        columns_hint: 'Підтримувані колонки (рядок-заголовок): ',
        columns_hint_files:
          'voice_file / video_file — імена файлів з вашої бібліотеки голосів та відео (не URL).',
        delimiter: 'Розділювач',
        delimiter_auto: 'Авто',
        delimiter_tab: 'Tab',
        delimiter_comma: 'Кома',
        url_label: 'Google Sheets або CSV/XLSX URL',
        url_hint:
          'Sheets — додайте сервісний акаунт у налаштуваннях. Інакше підтримуються прямі CSV/XLSX лінки.',
        errors_title: 'Помилки рядків',
        cancel: 'Скасувати',
        submit: 'Імпортувати',
        success: 'Імпортовано: {{n}} (пропущено: {{s}})',
        partial: 'Імпортовано {{n}}, є {{e}} помилок — перегляньте список',
        error: 'Не вдалося імпортувати',
        error_no_file: 'Оберіть файл',
        error_no_paste: 'Вставте дані',
        error_no_url: 'Вкажіть URL',
      },
      /* p2.* — verbatim labels ported from legacy `lang-data.js`
         (project-v2.html). Keys preserved so cross-referencing the
         legacy file remains painless. */
      p2: {
        back_to_projects: 'Проєкти',
        loading_project: 'Завантаження...',
        badge_empty: 'Порожній',
        subtitle_prep: 'Підготовка контенту та запуск обробки',
        sum_rows: 'Рядків:',
        sum_ready: 'Готові:',
        sum_running: 'У роботі:',
        sum_errors: 'Помилки:',
        sum_done: 'Готово:',
        sum_empty: 'Незаповнені:',
        label_el_model: 'Модель Eleven',
        label_video_engine: 'Відео-рушій',
        btn_check: 'Перевірити',
        btn_audio: 'Аудіо',
        btn_all: 'Все',
        btn_stop: 'Стоп',
        hint_selection:
          'Якщо рядки виділено, дії застосовуються до них. Якщо ні — до всіх готових рядків.',
        col_name: 'Назва',
        col_text: 'Текст',
        col_audio: 'Аудіо',
        col_video: 'Відео',
        col_el_model: 'EL Модель',
        col_fal_engine: 'FAL Рушій',
        col_num: '№',
        tasks_title: 'Завдання та результати',
        sse_label: 'оновлення',
        tasks_subtitle:
          'Тут відображається виконання рядків проєкту: статуси, помилки, результати та дії.',
        sel_count: 'Вибрано: {{n}}',
        btn_bulk_voice: 'Голос для вибраних',
        btn_bulk_video: 'Відео для вибраних',
        btn_delete_selected: 'Видалити вибрані',
        btn_add_row: 'Рядок',
        btn_import: 'Імпорт',
        btn_export: 'Експорт',
        btn_columns: 'Колонки',
        menu_rename: 'Перейменувати',
        menu_duplicate: 'Дублювати',
        menu_archive: 'Архівувати',
        menu_delete: 'Видалити проєкт',
        empty_title: 'У проєкті поки немає рядків',
        empty_sub: 'Додайте рядок вручну або імпортуйте дані',
        msg_rename_saved: 'Назву збережено',
        msg_project_archived: 'Проєкт архівовано',
        msg_project_deleted: 'Проєкт видалено',
        msg_project_duplicated: 'Проєкт продубльовано',
        msg_archive_err: 'Не вдалося архівувати проєкт',
        msg_delete_proj_err: 'Не вдалося видалити проєкт',
        msg_dup_err: 'Не вдалося дублювати проєкт',
        archive_title: 'Архівувати проєкт?',
        archive_body:
          'Проєкт буде переміщено до архіву. Ви зможете відновити його пізніше.',
        btn_archive_confirm: 'Архівувати',
        delete_proj_title: 'Видалити проєкт?',
        delete_proj_body:
          'Проєкт «{{title}}» буде видалено. Цю дію не можна скасувати.',
        btn_delete_confirm: 'Видалити',
      },
    },
    voices: {
      title: 'Голоси',
      sync_button: 'Синхронізувати з EL',
      sync_success: 'Синхронізовано',
      sync_error: 'Не вдалося синхронізувати',
      export_button: 'Експорт XLSX',
      filter: {
        all: 'Усі',
        pending: 'У черзі',
        ready: 'Готові',
        error: 'Помилка',
        orphaned: 'Осиротілі',
        deleted: 'Видалені',
      },
      status: {
        pending: 'У процесі клонування',
        ready: 'Готово',
        error: 'Помилка',
        orphaned: 'Осиротілий',
        deleted: 'Видалений',
      },
      column: {
        name: 'Назва',
        status: 'Статус',
        source: 'Джерело',
        tasks: 'Задачі',
        created: 'Створено',
        actions: 'Дії',
      },
      empty: 'У вас ще немає клонів голосу.',
      error_load: 'Не вдалося завантажити голоси.',
      no_results: 'Немає результатів за обраним фільтром.',
      task_count: '{{count}} задач',
      source_unavailable: 'Файл недоступний',
      stuck_badge: 'Завис',
      stuck_hint: 'Клонування триває занадто довго. Спробуйте видалити та створити заново.',
      action: {
        open_menu: 'Дії над голосом',
        rename: 'Перейменувати',
        delete: 'Видалити',
        reclone: 'Переклонувати',
      },
      rename: {
        title: 'Перейменувати голос',
        label: 'Назва',
        submit: 'Зберегти',
        cancel: 'Скасувати',
        success: 'Назву оновлено',
        error: 'Не вдалося перейменувати',
        error_required: 'Введіть назву',
      },
      delete: {
        confirm: {
          title: 'Видалити голос «{{name}}»?',
          body: 'Голос буде видалено з EL та локально. Цю дію не можна скасувати.',
          confirm: 'Видалити',
          cancel: 'Скасувати',
        },
        success: 'Голос видалено',
        error: 'Не вдалося видалити',
      },
      reclone: {
        success: 'Клонування запущено',
        source_missing:
          'Файл-джерело не знайдено. Створіть голос заново з нового аудіо.',
        invalid_status:
          'Переклонувати можна лише голоси зі статусом «осиротілий» або «помилка».',
        error: 'Не вдалося переклонувати',
      },
      tabs: {
        clones: 'Клони',
        history: 'EL історія',
      },
      sample: {
        play: 'Програти зразок',
      },
      upload: {
        title: 'Створити голос',
        drop_hint: 'Перетягніть аудіо-файл сюди або натисніть, щоб вибрати',
        constraints: 'mp3, wav, m4a, до {{maxMb}} МБ',
        choose: 'Вибрати файл',
        progress: 'Завантаження… {{percent}}%',
        success: 'Файл завантажено. Клонування запущено.',
        invalid_type: 'Підтримуються лише аудіо-файли.',
        invalid_extension: 'Розширення «{{ext}}» не підтримується.',
        too_large: 'Файл завеликий (макс {{maxMb}} МБ).',
        upload_error: 'Не вдалося завантажити файл.',
        clone_pending_hint:
          'Триває клонування… Список оновиться автоматично.',
      },
    },
    el_history: {
      title: 'EL історія',
      empty: 'Історія порожня.',
      error_load: 'Не вдалося завантажити історію.',
      load_more: 'Завантажити ще',
      column: {
        text: 'Текст',
        chars: 'Символи',
        created: 'Створено',
        actions: 'Дії',
      },
      play: 'Програти',
      chars_count: '{{count}} символів',
    },
    player: {
      now_playing: 'Зараз грає',
      play: 'Програти',
      pause: 'Пауза',
      close: 'Закрити',
      volume: 'Гучність',
      seek: 'Перемотати',
      load_error: 'Не вдалося відтворити',
    },
    templates: {
      title: 'Шаблони',
      subtitle:
        'Збережені аудіо та відео для повторного використання у проєктах та QT.',
      filter: {
        all: 'Усі',
        audio: 'Аудіо',
        video: 'Відео',
      },
      column: {
        name: 'Назва',
        type: 'Тип',
        category: 'Категорія',
        size: 'Розмір',
        used: 'Використано',
        created: 'Створено',
        actions: 'Дії',
      },
      type: {
        audio: 'Аудіо',
        video: 'Відео',
      },
      action: {
        pin: 'Закріпити',
        unpin: 'Відкріпити',
        delete: 'Видалити',
      },
      pinned: 'Закріплено',
      empty: 'Шаблонів ще немає.',
      no_results: 'Немає результатів за обраним типом.',
      error_load: 'Не вдалося завантажити шаблони.',
      use_count: '{{count}} разів',
      readonly_hint:
        'Шаблони перегляду — підключення до проєктів буде в наступному релізі.',
      upload_audio_button: 'Додати аудіо-шаблон',
      upload_video_button: 'Додати відео-шаблон',
      upload_success: 'Шаблон завантажено',
      upload_error: 'Не вдалося завантажити шаблон',
      update_error: 'Не вдалося оновити',
      delete_success: 'Шаблон видалено',
      delete_error: 'Не вдалося видалити',
      delete_confirm: {
        title: 'Видалити шаблон «{{name}}»?',
        body: 'Файл буде видалено з диска, якщо інші шаблони не посилаються на нього.',
        confirm: 'Видалити',
        cancel: 'Скасувати',
      },
    },
    workspace: {
      personal: 'Особистий простір',
      switched: 'Перемкнуто на {{name}}',
      switcher: {
        aria_label: 'Перемкнути workspace',
        manage: 'Керувати workspaces',
        create: 'Створити workspace',
      },
      tabs: {
        list: 'Workspaces',
        members: 'Учасники',
        settings: 'Налаштування',
      },
      settings: {
        title: 'Налаштування workspace',
        profile: 'Профіль',
        name: {
          label: 'Назва',
          error_too_short: 'Назва — від 2 до 100 символів',
          error_too_long: 'Назва — від 2 до 100 символів',
        },
        emoji: {
          label: 'Emoji',
        },
        save: 'Зберегти',
        saved: 'Збережено',
        save_error: 'Не вдалося зберегти зміни',
        readonly_hint:
          'Тільки власник або адміністратор може редагувати workspace.',
        unavailable_personal: 'Налаштування недоступні в особистому просторі.',
      },
      danger: {
        title: 'Небезпечна зона',
      },
      leave: {
        button: 'Залишити робочий простір',
        owner_hint: 'Передайте власність перед виходом.',
        pending_hint: 'Ця дія тимчасово недоступна — endpoint pending Go-fix.',
        confirm: {
          title: 'Залишити цей workspace?',
          body: 'Ви втратите доступ до проєктів, voices і файлів цього робочого простору. Цю дію не можна скасувати.',
          confirm: 'Залишити',
          cancel: 'Скасувати',
        },
        success: 'Ви залишили робочий простір',
        error: 'Не вдалося залишити workspace',
      },
      delete: {
        button: 'Видалити робочий простір',
        dialog: {
          title: 'Видалити «{{name}}»?',
          body:
            'Ця дія незворотна. Усі проєкти, voices, файли цього робочого простору будуть видалені.',
          confirm_label: 'Введіть «{{name}}» для підтвердження',
          confirm_placeholder: 'Назва workspace',
          confirm: 'Видалити назавжди',
          cancel: 'Скасувати',
        },
        success: 'Workspace видалено',
        error: 'Не вдалося видалити workspace',
      },
      members: {
        title: 'Учасники',
        empty: 'Поки що тільки ви.',
        personal_unavailable: 'Учасники недоступні в особистому просторі.',
        list_pending:
          'Список учасників — endpoint pending Go-fix. Запрошення/зміна ролі/видалення працюють.',
        error_load: 'Не вдалося завантажити учасників.',
        column: {
          name: 'Імʼя',
          email: 'Email',
          role: 'Роль',
          joined: 'Приєднався',
          actions: 'Дії',
        },
        role: {
          owner: 'Власник',
          admin: 'Адмін',
          member: 'Учасник',
        },
        action: {
          open_menu: 'Дії над учасником',
          change_role: 'Змінити роль',
          make_admin: 'Зробити адміном',
          make_member: 'Зробити учасником',
          remove: 'Видалити з workspace',
        },
        cannot_remove_owner: 'Власника не можна видалити',
        cannot_act_self: 'Дії над собою недоступні',
        change_role_success: 'Роль оновлено',
        change_role_error: 'Не вдалося змінити роль',
        remove_success: 'Учасника видалено',
        remove_error: 'Не вдалося видалити учасника',
        remove_confirm: {
          title: 'Видалити учасника?',
          body: 'Користувач втратить доступ до цього робочого простору. Цю дію не можна скасувати.',
          confirm: 'Видалити',
          cancel: 'Скасувати',
        },
      },
      invite: {
        button: 'Запросити',
        dialog: {
          title: 'Запросити учасника',
          role_label: 'Роль',
          role_hint:
            'Адмін може запрошувати, видаляти учасників і керувати workspace.',
          submit: 'Створити посилання',
          cancel: 'Скасувати',
          link_label: 'Посилання-запрошення',
          link_hint:
            'Скопіюйте та надішліть посилання користувачеві. Дійсне 7 днів.',
          copy: 'Скопіювати',
          copied: 'Скопійовано',
          close: 'Готово',
        },
        error: {
          generic: 'Не вдалося створити запрошення.',
          already_member: 'Цей користувач уже є учасником.',
        },
      },
    },
    workspaces: {
      title: 'Робочі простори',
      create_button: 'Створити workspace',
      empty_title: 'У вас немає workspaces. Створіть перший!',
      empty_cta: 'Створити перший',
      active_badge: 'Активний',
      switch_action: 'Перемкнути',
      settings_action: 'Налаштування',
      error_load: 'Не вдалося завантажити workspaces. Спробуйте ще раз',
      role: {
        owner: 'Власник',
        admin: 'Адмін',
        member: 'Учасник',
      },
      create_dialog: {
        title: 'Створити workspace',
        name_label: 'Назва',
        emoji_label: 'Emoji (необов’язково)',
        submit: 'Створити',
        cancel: 'Скасувати',
        success: 'Workspace створено',
        error_name_required: 'Введіть назву (1–50 символів)',
        error_name_taken: 'Workspace з такою назвою вже існує',
      },
    },
    data: {
      title: 'Дані',
      subtitle: 'Усі рядки проєктів. Слідкуй за прогресом, переглядай і завантажуй результати.',
      filter: {
        status_label: 'Статус',
        date_label: 'Дата',
        project_label: 'Проєкт',
        project_all: 'Усі проєкти',
        status: {
          all: 'Усі',
          queued: 'У черзі',
          running: 'Виконується',
          done: 'Готово',
          failed: 'Помилка',
          cancelled: 'Скасовано',
        },
        date: {
          all: 'Усі',
          today: 'Сьогодні',
          yesterday: 'Вчора',
          '3d': '3 дні',
          '7d': '7 днів',
          '30d': '30 днів',
        },
      },
      column: {
        name: 'Назва',
        text: 'Текст',
        el_status: 'EL',
        fal_status: 'FAL',
        overall: 'Статус',
        updated: 'Оновлено',
        actions: 'Дії',
      },
      action: {
        play_audio: 'Програти аудіо',
        play_video: 'Переглянути відео',
        download: 'Завантажити',
      },
      empty: 'Немає рядків за вибраними фільтрами.',
      error_load: 'Не вдалося завантажити список.',
      download_error: 'Не вдалося завантажити',
      pagination: {
        summary: 'Сторінка {{page}} з {{total_pages}} • Усього: {{total}}',
        prev: 'Попередня',
        next: 'Наступна',
      },
      transfer_template: {
        title: 'Перенести як шаблон',
        source_hint: 'На основі #{{n}}{{name}}',
        target_label: 'Куди перенести',
        target_qt: 'Швидкі завдання',
        target_qt_hint: 'Новий рядок у QT — без проєкту',
        target_project: 'Проєкт',
        target_project_hint: 'Новий рядок у вибраному проєкті',
        no_projects: 'Немає доступних проєктів',
        confirm: 'Перенести',
        success: 'Шаблон створено — переходимо до редагування',
        error: 'Не вдалося створити шаблон',
        error_no_target: 'Оберіть, куди перенести',
        note_prefix: 'Створено на основі',
      },
      clone_restart: {
        success: 'Створено нову задачу зі змінами',
        error: 'Не вдалося створити нову задачу',
      },
    },
    admin: {
      title: 'Адмін-панель',
      subtitle: 'Користувачі, очікувальні реєстрації та запити на скидання пароля.',
      error_load: 'Не вдалося завантажити',
      error_action: 'Не вдалося виконати дію',
      tab: {
        users: 'Користувачі',
        pending: 'Очікують схвалення',
        resets: 'Скидання паролів',
      },
      users: {
        search_placeholder: 'Пошук по email або імені…',
        empty: 'Користувачів не знайдено.',
        status: {
          all: 'Усі',
          active: 'Активні',
          pending: 'Очікують',
          blocked: 'Заблоковані',
        },
        chip: {
          active: 'активний',
          pending: 'очікує',
          blocked: 'заблокований',
        },
        column: {
          user: 'Користувач',
          role: 'Роль',
          status: 'Статус',
          tasks: 'Задач',
          projects: 'Проєктів',
          created: 'Створено',
          actions: 'Дії',
        },
        action: {
          approve: 'Схвалити',
          block: 'Заблокувати',
          unblock: 'Розблокувати',
          promote: 'Зробити адміном',
          demote: 'Зняти права адміна',
          delete: 'Видалити',
        },
        approve_success: 'Користувача схвалено',
        block_success: 'Заблоковано',
        unblock_success: 'Розблоковано',
        promote_success: 'Призначено адміном',
        demote_success: 'Знято права адміна',
        demote_self_blocked: 'Не можна зняти права адміна з власного акаунту',
        delete_success: 'Видалено',
        delete_confirm: {
          title: 'Видалити «{{email}}»?',
          body: 'Користувач та усі його дані будуть позначені видаленими. Дію неможливо скасувати.',
          confirm: 'Видалити',
          cancel: 'Скасувати',
        },
        pagination: {
          summary: 'Сторінка {{page}} з {{total_pages}} • Усього: {{total}}',
          prev: 'Попередня',
          next: 'Наступна',
        },
      },
      pending: {
        empty: 'Заявок на реєстрацію немає.',
        registered_at: 'Зареєстровано: {{date}}',
        approve_button: 'Схвалити',
      },
      resets: {
        empty: 'Запитів на скидання пароля немає.',
        requested_at: 'Запитано: {{date}}',
        has_tg: 'Telegram підключено',
        no_tg: 'Немає Telegram — передайте пароль вручну',
        generate_button: 'Згенерувати пароль',
        generated_success: 'Пароль згенеровано й надіслано в Telegram',
        generated_no_tg: 'Пароль згенеровано — Telegram не підключено, передайте вручну',
        generated_tg_failed:
          'Пароль згенеровано, але Telegram не прийняв повідомлення — передайте вручну',
        password_for: 'Пароль для {{user}}:',
        copy_hint: 'Передайте цей пароль користувачу:',
        copy: 'Скопіювати',
        copied: 'Скопійовано',
        copy_error: 'Не вдалося скопіювати',
      },
    },
    overlay: {
      title: 'Блики (Frame Overlay)',
      subtitle: 'Накладай картинки на окремі кадри відео — додає «живий» ефект, щоб ролик не виглядав 100% машинним.',
      videos_section: 'Відео (до {{max}} штук)',
      images_section: 'Картинки-блики',
      videos_empty: 'Перетягніть або виберіть до 10 відео',
      images_empty: 'Виберіть 1-3 картинки PNG/JPEG',
      add_button: 'Додати',
      remove: 'Прибрати',
      frames_mode_label: 'Щільність',
      frames_mode_1: 'Розріджена',
      frames_mode_2: 'Щільна',
      frames_mode_hint: '1 = кожен N-й кадр, 2 = частіше',
      run_button: 'Обробити',
      upload_success: 'Задачі створено',
      upload_error: 'Не вдалося запустити',
      error_no_videos: 'Додайте хоча б одне відео',
      error_no_images: 'Додайте хоча б одну картинку',
      error_load: 'Не вдалося завантажити список',
      empty: 'Задач ще немає.',
      tasks_title: 'Задачі обробки',
      expires_in: 'видалиться через {{h}} год',
      action: {
        play: 'Переглянути',
        download: 'Завантажити',
        delete: 'Видалити',
      },
      delete_confirm: {
        title: 'Видалити цю задачу?',
        body: 'Файл буде видалено. Перед цим завантаж результат, якщо потрібно.',
        confirm: 'Видалити',
        cancel: 'Скасувати',
      },
      delete_success: 'Видалено',
      delete_error: 'Не вдалося видалити',
      download_error: 'Не вдалося завантажити',
      play_error: 'Не вдалося відтворити',
    },
    transcription: {
      title: 'Транскрипція',
      subtitle: 'Завантаж аудіо або відео — отримай текст з розпізнаванням спікерів',
      language_label: 'Мова',
      lang_auto: 'Автовизначення',
      upload_button: 'Завантажити файл',
      upload_success: 'Відправлено {{n}} файлів на транскрипцію',
      upload_error: 'Помилка завантаження',
      sending: 'Завантаження...',
      empty: 'Транскрипцій поки немає — завантаж файл вище',
      error_load: 'Не вдалося завантажити список.',
      provider_label: 'Обробка:',
      provider: {
        el: 'ElevenLabs',
        el_hint: 'за підпискою EL',
        whisper: 'Whisper',
        whisper_hint: '~$0.01 / хв',
        el_fal: 'EL Scribe',
        el_fal_hint: '$0.008 / хв',
      },
      drop_label: 'Перетягни файли сюди або клікни',
      drop_hint: 'MP3, WAV, M4A, MP4, MOV — до 300 МБ · максимум 3 файли',
      select_files: 'Обрати файли',
      selected_files: 'Обрані файли',
      clear: 'Очистити',
      translate_after: 'Перекласти після транскрипції',
      lang_ru: 'Російська',
      lang_uk: 'Українська',
      send: 'Відправити',
      feat_langs: '90+ мов',
      feat_speakers: 'Розпізнавання спікерів',
      feat_translate: 'Автопереклад',
      max_files_warn: 'Максимум {{n}} файлів за раз',
      file_too_large: 'Файл занадто великий (макс. {{n}} МБ)',
      no_files_warn: 'Обери файли для транскрипції',
      results: 'Результати',
      refresh: 'Оновити',
      column: {
        date: 'Дата',
        file: 'Файл',
        duration: 'Трив.',
        language: 'Мова',
        original: 'Оригінал',
        translation: 'Переклад',
        status: 'Статус',
        actions: 'Дії',
        created: 'Створено',
      },
      status_label: {
        queued: 'В черзі',
        pending: 'В черзі',
        uploading: 'Завантаження',
        transcribing: 'Транскрипція',
        running: 'Транскрипція',
        translating: 'Переклад',
        done: 'Готово',
        failed: 'Помилка',
        error: 'Помилка',
        cancelled: 'Скасовано',
      },
      action: {
        view: 'Відкрити',
        delete: 'Видалити',
        retry: 'Перезапустити',
        download: 'Завантажити',
      },
      delete_confirm: {
        title: 'Видалити цю транскрипцію?',
        body: 'Запис буде помічено як видалений. Файл на диску прибере фоновий cleanup.',
        confirm: 'Видалити',
        cancel: 'Скасувати',
      },
      delete_success: 'Транскрипцію видалено',
      delete_error: 'Помилка видалення',
      retry_success: 'Запущено повторно',
      retry_error: 'Не вдалося перезапустити',
      translate_started: 'Переклад запущено',
      translate_error: 'Помилка перекладу',
      error_human: {
        fal_balance: 'Закінчився баланс FAL. Поповни рахунок на fal.ai/dashboard/billing і спробуй знову.',
        fal_auth: 'Невірний API-ключ FAL. Перевір ключ у Налаштуваннях → API ключі.',
        el_auth: 'Невірний API-ключ ElevenLabs. Перевір ключ у Налаштуваннях → API ключі.',
        el_quota: 'Закінчилися кредити ElevenLabs. Поповни підписку на elevenlabs.io.',
        rate_limit: 'Сервіс отримав забагато запитів. Зачекай 1–2 хвилини і натисни ↻ ще раз.',
        server: 'Сервіс тимчасово недоступний. Спробуй через кілька хвилин.',
        timeout: 'Час очікування вичерпано. Файл міг бути занадто довгим — спробуй ще раз.',
        network: 'Помилка мережі. Перевір інтернет і натисни ↻.',
        file_missing: 'Оригінальний файл уже видалено зі сховища. Завантаж файл заново.',
        no_key: 'API-ключ не налаштовано. Додай його в Налаштуваннях → API ключі.',
        unknown: 'Невідома помилка. Натисни ↻ або звернись до підтримки.',
        details_prefix: 'Деталі',
      },
      popup_original: 'Оригінал',
      popup_translation: 'Переклад',
      no_text: 'Текст відсутній',
      no_translation_yet: 'Перекладу ще немає — вибери мову нижче й натисни «Переклад».',
      copy: 'Копіювати',
      copied: 'Скопійовано',
      copy_error: 'Не вдалося скопіювати',
      download_txt: 'Завантажити .txt',
      chars: '{{n}} символів',
    },
    translate: {
      title: 'Переклад',
      subtitle: 'Автоматичний переклад тексту через DeepL API',
      no_key_title: 'Немає DeepL ключа',
      no_key_hint: 'Додай ключ у Налаштування → API ключі (провайдер: deepl).',
      source_label: 'З мови',
      target_label: 'На мову',
      auto_detect: 'Визначити мову',
      swap: 'Поміняти місцями',
      swap_title: 'Поміняти мови місцями',
      swap_auto_warn: 'Вкажіть мову джерела для обміну',
      input_label: 'Оригінал',
      input_placeholder: 'Введіть текст для перекладу…',
      output_label: 'Переклад',
      output_placeholder: 'Тут зʼявиться переклад',
      source_ph: 'Введіть текст для перекладу...',
      result_ph: 'Переклад з\'явиться тут...',
      detected: '(виявлено: {{lang}})',
      chars: 'символів',
      run_button: 'Перекласти',
      translate_btn: 'Перекласти',
      translate_fail: 'Помилка перекладу',
      translating: 'Переклад виконується...',
      mode_auto: 'Авто',
      mode_btn: 'Кнопка',
      max_chars: 'Максимум 10 000 символів за один запит',
      shortcut_hint: '— швидкий переклад',
      clear: 'Очистити',
      copy: 'Копіювати переклад',
      copied: 'Скопійовано в буфер обміну',
      copy_error: 'Не вдалося скопіювати',
      key_rotated: 'Перемкнуто на наступний DeepL ключ',
      error_empty: 'Введіть текст',
      error_call: 'Не вдалося перекласти',
      usage_label: 'Використано символів',
      usage_title: 'Налаштування DeepL ключа',
      search_lang: 'Знайти мову...',
      recent: 'Останні',
      suggested: 'Запропоновані',
      all_langs: 'Усі мови',
      results_n: '{{n}} результатів',
      no_results: 'Нічого не знайдено для «{{q}}»',
      nokey_title: 'DeepL API ключ не налаштований',
      nokey_desc: 'Для використання перекладу потрібен DeepL API ключ.<br>Додай його в налаштуваннях акаунту.',
      go_settings: 'Перейти до налаштувань',
    },
    quick: {
      title: 'Швидкі завдання',
      subtitle: 'Створи відео без проєкту — завантаж голоси та відео, додай тексти і запусти',
      audio_section: 'Аудіо (зразок голосу)',
      video_section: 'Відео (опційно — для lip-sync)',
      upload_button: 'Завантажити',
      no_files: 'Файлів ще немає',
      texts_label: 'Тексти (один на рядок)',
      texts_placeholder: 'Привіт, Іване!\nПривіт, Маріє!\nПривіт, Олегу!',
      run_button: 'Запустити',
      file_delete: 'Видалити файл',
      delete_file_confirm: {
        title: 'Видалити «{{name}}»?',
        body: 'Файл буде видалено. Якщо він використовується в активних задачах — задачі продовжать використовувати копію.',
        confirm: 'Видалити',
        cancel: 'Скасувати',
      },
      delete_row_confirm: {
        title: 'Видалити рядок?',
        body: 'Текст: «{{text}}». Активні задачі цього рядка будуть скасовані.',
        confirm: 'Видалити',
        cancel: 'Скасувати',
      },
      upload_success_audio: 'Аудіо завантажено',
      upload_success_video: 'Відео завантажено',
      upload_error: 'Не вдалося завантажити файл',
      delete_error: 'Не вдалося видалити',
      run_error_no_files: 'Виберіть принаймні один файл',
      run_error_no_text: 'Додайте хоча б один текст',
      run_success: 'Створено {{n}} рядків',
      run_error: 'Не вдалося запустити',
      download_error: 'Не вдалося завантажити',
      tasks_title: 'Останні задачі',
      tasks_empty: 'Запущених задач ще немає.',
      task: {
        text: 'Текст',
        status: 'Статус',
        actions: 'Дії',
        play: 'Програти аудіо',
        play_video: 'Переглянути відео',
        download: 'Завантажити',
        delete: 'Видалити рядок',
      },
      // ── Legacy `qt.*` keys ported verbatim from `lang-data.js` ────────
      add_package: '+ Додати пакет',
      texts_to_all: 'Тексти → в усі пакети',
      texts_to_all_title: 'Скопіювати тексти першого пакету в усі інші',
      settings: 'Налаштування',
      run_all: '▶ Запустити все',
      run_audio: 'Тільки голоси',
      stop_all: '⏹ СТОП ВСЕ',
      results: 'Результати',
      empty: 'Завдань поки немає — запусти перше завдання вище',
      // ── React-only namespaced keys used by hooks / wrappers ──────────
      action: { clear_done: 'Очистити завершені' },
      mode: {
        clone: 'Клон голосу',
        ready_voice: 'Готовий голос',
        templates: 'З шаблонів',
        section_label: 'Режим швидкої задачі',
      },
      mode_hint: {
        clone: 'Завантаж аудіо-референс для клонування голосу + тексти для озвучки.',
        ready_voice:
          'Обери раніше склонований голос + відео — без витрат на повторне клонування.',
        templates: 'Готові шаблони аудіо/відео з твоєї бібліотеки.',
      },
    },
  },
}
